(() => {
  'use strict';

  const doc = document;
  const body = doc.body;
  if (!body) return;

  const qs = (selector, context = doc) => context.querySelector(selector);
  const qsa = (selector, context = doc) => [...context.querySelectorAll(selector)];
  const jsUrl = (url) => url.endsWith('.js') ? url : `${url}.js`;
  const routes = {
    cart: body.dataset.cartUrl || '/cart',
    add: body.dataset.cartAddUrl || '/cart/add',
    change: body.dataset.cartChangeUrl || '/cart/change'
  };
  const currency = body.dataset.currency || 'GBP';

  const money = (cents) => {
    const number = Number(cents) || 0;
    try {
      return new Intl.NumberFormat(doc.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency,
        minimumFractionDigits: number % 100 === 0 ? 0 : 2,
        maximumFractionDigits: 2
      }).format(number / 100);
    } catch (_) {
      return (number / 100).toFixed(2);
    }
  };

  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  }[character]));

  // Mobile navigation.
  qsa('[data-menu-toggle]').forEach((button) => {
    const header = button.closest('[data-header]');
    const menu = header ? qs('[data-mobile-menu]', header) : null;
    if (!menu) return;

    const close = () => {
      menu.hidden = true;
      button.setAttribute('aria-expanded', 'false');
      body.classList.remove('f-nav-open');
    };

    const open = () => {
      const bottom = header?.getBoundingClientRect().bottom || 0;
      menu.style.top = `${Math.max(0, Math.round(bottom))}px`;
      menu.hidden = false;
      button.setAttribute('aria-expanded', 'true');
      body.classList.add('f-nav-open');
      qs('a,button', menu)?.focus();
    };

    button.addEventListener('click', () => menu.hidden ? open() : close());
    qsa('a', menu).forEach((link) => link.addEventListener('click', close));
    menu.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab' || menu.hidden) return;
      const focusable = qsa('a[href],button:not([disabled]),[tabindex]:not([tabindex="-1"])', menu)
        .filter((element) => !element.hidden);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && doc.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && doc.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
    doc.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !menu.hidden) {
        close();
        button.focus();
      }
    });
  });

  const getModuleState = (module) => {
    const select = qs('[data-variant-select]', module);
    const option = select ? select.options[select.selectedIndex] : null;
    const available = option ? option.dataset.available === 'true' : module.dataset.currentAvailable === 'true';
    const variantId = option?.value || module.dataset.currentVariant || '';
    const cents = option?.dataset.price || module.dataset.currentPrice || '';
    const formattedPrice = option?.dataset.priceFormatted || module.dataset.currentPriceFormatted || (cents ? money(cents) : '');
    return { select, option, available, variantId, cents, formattedPrice };
  };

  const updateProductUrl = (module, variantId) => {
    if (!variantId || !module.dataset.productUrl || !window.history?.replaceState) return;
    try {
      const productUrl = new URL(module.dataset.productUrl, window.location.origin);
      if (window.location.pathname !== productUrl.pathname) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({ variant: variantId }, '', `${url.pathname}${url.search}${url.hash}`);
    } catch (_) {
      // URL enhancement is non-essential; native Shopify form submission remains unaffected.
    }
  };

  const setButtonState = (module, { updateUrl = false } = {}) => {
    const button = qs('[data-add-button]', module);
    const label = qs('[data-add-label]', module);
    const price = qs('[data-variant-price]', module);
    const dynamicCheckout = qs('[data-dynamic-checkout]', module);
    if (!button) return;

    const state = getModuleState(module);
    module.dataset.currentAvailable = String(state.available);
    if (state.variantId) module.dataset.currentVariant = state.variantId;
    if (state.cents) module.dataset.currentPrice = state.cents;
    if (state.formattedPrice) module.dataset.currentPriceFormatted = state.formattedPrice;

    const baseLabel = button.dataset.baseLabel || 'Acquire No.1';
    const soldOutLabel = button.dataset.soldoutLabel || 'Sold out';
    const showPrice = button.dataset.showPrice === 'true';

    button.disabled = !state.available;
    button.setAttribute('aria-disabled', String(!state.available));
    if (label) label.textContent = state.available
      ? `${baseLabel}${showPrice && state.formattedPrice ? ` — ${state.formattedPrice}` : ''}`
      : soldOutLabel;
    if (price && state.formattedPrice) price.textContent = state.formattedPrice;
    if (dynamicCheckout) dynamicCheckout.hidden = !state.available;

    if (module.dataset.productId) {
      qsa(`[data-sticky-price][data-product-id="${module.dataset.productId}"]`).forEach((element) => {
        if (state.formattedPrice) element.textContent = state.formattedPrice;
      });
      qsa(`[data-sticky-acquire][data-product-id="${module.dataset.productId}"]`).forEach((element) => {
        const stickyBase = element.dataset.baseLabel || baseLabel;
        const stickySold = element.dataset.soldoutLabel || soldOutLabel;
        const stickyPrice = element.dataset.showPrice === 'true';
        element.textContent = state.available
          ? `${stickyBase}${stickyPrice && state.formattedPrice ? ` — ${state.formattedPrice}` : ''}`
          : stickySold;
        element.disabled = !state.available;
        element.setAttribute('aria-disabled', String(!state.available));
      });
    }

    if (updateUrl) updateProductUrl(module, state.variantId);
  };

  // Variant selectors and availability.
  qsa('[data-buy-module]').forEach((module) => {
    const select = qs('[data-variant-select]', module);
    if (select) select.addEventListener('change', () => setButtonState(module, { updateUrl: true }));
    setButtonState(module);
  });

  // Mobile/sticky purchase controls scroll to the actual Shopify product form.
  qsa('[data-sticky-acquire]').forEach((button) => {
    button.addEventListener('click', () => {
      if (button.disabled) return;
      const selector = button.dataset.scrollBuy;
      const target = selector ? qs(selector) : null;
      if (!target) return;
      target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
      window.setTimeout(() => qs('[data-add-button]', target)?.focus({ preventScroll: true }), 350);
    });
  });

  // Mobile product gallery: make the scroll-snap carousel usable from a keyboard.
  qsa('.f-product-page__gallery').forEach((gallery) => {
    gallery.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key) || gallery.scrollWidth <= gallery.clientWidth) return;
      event.preventDefault();
      gallery.scrollBy({
        left: (event.key === 'ArrowRight' ? 1 : -1) * gallery.clientWidth * 0.86,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    });
  });

  // Cart drawer.
  const drawer = qs('[data-cart-drawer]');
  const overlay = qs('[data-cart-overlay]');
  const drawerItems = qs('[data-cart-items]');
  const drawerFooter = qs('[data-cart-footer]');
  const drawerTotal = qs('[data-cart-total]');
  const drawerStatus = qs('[data-cart-status]');
  let returnFocus = null;

  const setCartCount = (count) => qsa('[data-cart-count]').forEach((element) => { element.textContent = count; });

  const lineMarkup = (item, line) => {
    const imageUrl = item.image ? `${item.image}${item.image.includes('?') ? '&' : '?'}width=192` : '';
    const image = imageUrl ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(item.product_title)}" width="96" height="96" loading="lazy">` : '';
    const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<div class="f-cart-line__meta">${escapeHtml(item.variant_title)}</div>` : '';
    return `<article class="f-cart-line">
      <a href="${escapeHtml(item.url)}">${image}</a>
      <div>
        <a href="${escapeHtml(item.url)}"><h3>${escapeHtml(item.product_title)}</h3></a>
        ${variant}
        <div class="f-cart-line__foot">
          <div class="f-cart-qty" aria-label="Quantity for ${escapeHtml(item.product_title)}">
            <button type="button" data-cart-line="${line}" data-cart-qty="${Math.max(0, item.quantity - 1)}" aria-label="Decrease quantity">−</button>
            <span aria-live="polite">${item.quantity}</span>
            <button type="button" data-cart-line="${line}" data-cart-qty="${item.quantity + 1}" aria-label="Increase quantity">+</button>
          </div>
          <span class="f-cart-line__price">${money(item.final_line_price)}</span>
        </div>
      </div>
    </article>`;
  };

  const renderCart = (cart) => {
    setCartCount(cart.item_count || 0);
    if (drawerStatus) drawerStatus.textContent = '';
    if (!drawerItems || !drawerFooter) return;
    if (!cart.items?.length) {
      drawerItems.innerHTML = `<p class="f-cart-empty">${escapeHtml(drawer?.dataset.emptyText || '')}</p>`;
      drawerFooter.hidden = true;
      return;
    }
    drawerItems.innerHTML = cart.items.map((item, index) => lineMarkup(item, index + 1)).join('');
    if (drawerTotal) drawerTotal.textContent = money(cart.total_price);
    drawerFooter.hidden = false;
  };

  const fetchCart = async () => {
    const response = await fetch(jsUrl(routes.cart), { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to load bag');
    const cart = await response.json();
    renderCart(cart);
    return cart;
  };

  const openCart = async (trigger, refresh = true) => {
    if (!drawer || !overlay) {
      window.location.href = routes.cart;
      return;
    }
    returnFocus = trigger || doc.activeElement;
    if (refresh) {
      try {
        await fetchCart();
      } catch (_) {
        if (drawerStatus) drawerStatus.textContent = drawer.dataset.errorText || '';
      }
    }
    overlay.hidden = false;
    drawer.inert = false;
    drawer.setAttribute('aria-hidden', 'false');
    body.classList.add('f-cart-open');
    qs('[data-cart-close]', drawer)?.focus();
  };

  const closeCart = () => {
    if (!drawer || !overlay) return;
    drawer.setAttribute('aria-hidden', 'true');
    drawer.inert = true;
    overlay.hidden = true;
    body.classList.remove('f-cart-open');
    if (returnFocus && typeof returnFocus.focus === 'function') returnFocus.focus();
  };

  qsa('[data-cart-open]').forEach((link) => link.addEventListener('click', (event) => {
    event.preventDefault();
    openCart(link);
  }));
  qs('[data-cart-close]')?.addEventListener('click', closeCart);
  overlay?.addEventListener('click', closeCart);

  doc.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && drawer?.getAttribute('aria-hidden') === 'false') closeCart();
  });

  drawer?.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab' || drawer.getAttribute('aria-hidden') !== 'false') return;
    const focusable = qsa('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])', drawer).filter((element) => !element.hidden);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && doc.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && doc.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  drawer?.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-cart-line]');
    if (!button) return;
    button.disabled = true;
    if (drawerStatus) drawerStatus.textContent = '';
    try {
      const response = await fetch(jsUrl(routes.change), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(button.dataset.cartLine), quantity: Number(button.dataset.cartQty) })
      });
      if (!response.ok) throw new Error('Unable to update bag');
      renderCart(await response.json());
    } catch (_) {
      if (drawerStatus) drawerStatus.textContent = drawer?.dataset.errorText || 'Unable to update bag.';
    } finally {
      button.disabled = false;
    }
  });

  // Progressive Ajax add-to-cart. The native Shopify product form remains the fallback.
  qsa('[data-product-form]').forEach((form) => {
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const module = form.closest('[data-buy-module]') || form;
      const button = qs('[data-add-button]', form);
      const label = qs('[data-add-label]', button || form);
      const status = qs('[data-form-status]', module);
      if (!button || button.disabled) return;

      const state = getModuleState(module);
      if (!state.available) {
        setButtonState(module);
        return;
      }

      const previousLabel = label?.textContent || '';
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('is-loading');
      if (label && button.dataset.loadingLabel) label.textContent = button.dataset.loadingLabel;
      if (status) status.textContent = '';

      try {
        const response = await fetch(jsUrl(routes.add), {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        });
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          if (status) status.textContent = error.description || error.message || drawer?.dataset.errorText || 'Unable to add this item.';
          return;
        }
        await fetchCart();
        await openCart(button, false);
      } catch (_) {
        HTMLFormElement.prototype.submit.call(form);
        return;
      } finally {
        button.classList.remove('is-loading');
        if (label && previousLabel) label.textContent = previousLabel;
        setButtonState(module);
      }
    });
  });
})();
